import { type FC, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";

import { FetchPost } from "@/lib/fetch-helper";
import { languageSelector, useViewportInfoContext } from "@/src/contexts";
import type { IDiscountNavigationResult } from "@/src/discount/DiscountNavigation";
import { add } from "@/src/discount/DiscountNavigationLib";
import type { IDiscountsPanel, IDiscountsPanelResult } from "@/src/discount/DiscountsPanel/DiscountsPanel";
import { useQueryCustomNoSuspense } from "@/src/query-client-overrides";
import type { PriceComparisonItemProps } from "@/src/search/PriceComparison/PriceComparisonItem";
import PriceComparisonItem from "@/src/search/PriceComparison/PriceComparisonItem";
import SearchGroup from "@/src/search/SearchGroup";
import { Loading, LoadingAppearance } from "@Components/Loading";
import { urlFormat } from "@Components/Modules/Helpers";
import { Device } from "@Components/Modules/ViewportResize";
import TelemetryHoc from "@Components/telemetry-hoc/telemetry-hoc";
import { Search } from "@Routes/SearchLinks";
import * as stylex from "@stylexjs/stylex";

import { type QuerySmMax, type QueryXsMax, colors, genericStyles } from "../../styles/tokens.stylex";
import LogoTile, { type ILogoTile } from "../common/LogoTile";
import DiscountsPanel from "../discount/DiscountsPanel";
import Resources from "./Search.Resources";
import ResourcesEn from "./Search.Resources.En";

import * as cssPortal from "../shared/Portal.module.css";
import * as css from "./SearchResults.module.css";

export interface ISearchResults {
    results: IDiscountsPanel;
    noResultsText: string;
}

interface ISearchResultsResult {
    discounts: IDiscountsPanelResult;
    brands: ILogoTile[];
    products: PriceComparisonItemProps[];
    productsCount: number;
    navigation: IDiscountNavigationResult;
}

export const SearchResults: FC<ISearchResults> = ({ noResultsText, results }) => {
    const resources = languageSelector(Resources, ResourcesEn);
    const [searchParams] = useSearchParams();
    const [expandBrands, setExpandBrands] = useState(false);
    const [carouselsToDisplay, setCarouselsToDisplay] = useState(2);

    const search = searchParams.get("search");

    const { device, isMobile, isMobileOrTablet, isLaptop, isTablet } = useViewportInfoContext();

    const groupAlone = !isMobileOrTablet;
    const queryEndpoint = urlFormat(Search, {
        action: "Results",
    });

    const { isLoading, data } = useQueryCustomNoSuspense<ISearchResultsResult>({
        queryKey: [queryEndpoint, search, groupAlone],
        queryFn: () =>
            FetchPost<ISearchResultsResult>(queryEndpoint, {
                search,
                groupAlone: groupAlone,
                viewport: Device[device],
            }),
    });

    const { brands, discounts, productsCount, products } = data || {};

    useEffect(() => {
        if (data?.navigation) {
            add(data.navigation);
        }
    }, [data]);

    const noResults =
        !isLoading && !(brands?.length > 0 || discounts?.carousels?.length > 0 || productsCount > 0);
    const totalDiscounts = discounts?.carousels.reduce((acc, c) => c.discounts.length + acc, 0);
    const currentCount = discounts?.carousels
        .slice(0, carouselsToDisplay)
        .reduce((acc, c) => c.discounts.length + acc, 0);

    let brandsCount = 12;

    if (expandBrands && brands) {
        brandsCount = brands.length;
    } else {
        brandsCount = isMobile ? 4 : isTablet ? 6 : isLaptop ? 10 : brandsCount;
    }

    return (
        <div className={css.searchResultsComponent}>
            <div className={cssPortal.standardContainerV2}>
                <div className={css.setSectionColOne}>
                    {isLoading ? (
                        <Loading appearance={LoadingAppearance.Small} />
                    ) : (
                        <>
                            {noResults && (
                                <div {...stylex.props(styles.noResults, styles.border)}>{noResultsText}</div>
                            )}
                            {brands?.length > 0 && (
                                <SearchGroup
                                    text={resources.showMoreBrands}
                                    title={resources.brand}
                                    count={brands.length}
                                    currentCount={brandsCount}
                                    button={{
                                        as: "button",
                                        variant: "transparent",
                                        size: "M",
                                        onClick: () => {
                                            setExpandBrands(true);
                                        },
                                    }}
                                >
                                    <div className={css.brandsContainer}>
                                        {brands.slice(0, brandsCount).map((brand: ILogoTile) => (
                                            <LogoTile
                                                {...brand}
                                                key={brand.brandName}
                                                styleX={styles.customLogoTile}
                                                onscreenTrack={{
                                                    origin: "SearchResult",
                                                    itemId: brand.id,
                                                    eventName: "BrandView",
                                                    actionType: "View",
                                                }}
                                            />
                                        ))}
                                    </div>
                                </SearchGroup>
                            )}
                            {productsCount > 0 && (
                                <SearchGroup
                                    text={resources.showMoreProducts}
                                    title={resources.products}
                                    count={productsCount}
                                    currentCount={products.length}
                                    button={{
                                        as: "a",
                                        size: "M",
                                        href: `/kategorie?search=${encodeURIComponent(search)}&p=1&size=${isMobile ? 30 : 60}&sort=popularity`,
                                        eventName: "prcomp-searchShowAllProducts",
                                        origin: "searchResultsGlobal",
                                        isExternal: true,
                                    }}
                                >
                                    <div {...stylex.props(styles.productContainer)}>
                                        {products.map((p, i) => (
                                            <TelemetryHoc
                                                key={p.id}
                                                telemetryCustom={{
                                                    eventName: "prcomp-product",
                                                    actionType: "Display",
                                                    itemId: `${p.id}`,
                                                    param1: `${i + 1}`,
                                                    origin: "searchResultsGlobal",
                                                }}
                                            >
                                                <PriceComparisonItem
                                                    {...p}
                                                    position={i + 1}
                                                    mobileView={isMobile}
                                                />
                                            </TelemetryHoc>
                                        ))}
                                    </div>
                                </SearchGroup>
                            )}
                            {discounts && (
                                <SearchGroup
                                    text={resources.showMoreOffers}
                                    title={resources.offers}
                                    count={totalDiscounts}
                                    currentCount={currentCount}
                                    button={{
                                        as: "button",
                                        variant: "transparent",
                                        size: "M",
                                        onClick: () => {
                                            setCarouselsToDisplay(discounts.carousels.length);
                                        },
                                    }}
                                >
                                    <DiscountsPanel
                                        key={carouselsToDisplay}
                                        {...results}
                                        initialData={{
                                            carousels: discounts.carousels.slice(0, carouselsToDisplay),
                                        }}
                                    />
                                </SearchGroup>
                            )}
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

const xsMax: QueryXsMax = "@media (max-width: 759px)";
const smMax: QuerySmMax = "@media (max-width: 989px)";

const styles = stylex.create({
    productContainer: {
        gap: "0.625rem",
        display: "grid",
        gridTemplateColumns: {
            default: "repeat(4, minmax(0, 1fr))",
            [xsMax]: "repeat(2, minmax(0, 1fr))",
        },
    },

    customLogoTile: {
        height: "clamp(175px, 185px, 210px)",
    },

    border: {
        borderRadius: genericStyles.borderRadiusS1,
    },

    noResults: {
        margin: { default: "45px 0", [smMax]: "28px 0 36px 0" },
        borderColor: "#eee",
        borderStyle: "solid",
        borderWidth: 1,
        paddingBlock: "70px",
        paddingInline: "0",
        color: colors.fg_Primary,
        fontSize: "1.5rem",
        textAlign: "center",
    },
});
